import React, { useEffect, useState } from 'react';
import { Package, Loader2, History } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supplierProductsAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import PriceHistoryPanel from '@/components/PriceHistoryPanel';
import ProductReceptionsDrawer from '@/components/ProductReceptionsDrawer';

/**
 * Read-only preview dialog for a supplier product.
 * Loads its own data via /api/supplier-products/detail/{id}.
 * Mirrors the visual layout of ProductsDialogs.jsx `spDetailProduct` dialog.
 *
 * Props:
 *  - supplierProductId: string | null  (null → closed)
 *  - onClose: () => void
 */
export default function SupplierProductPreviewDialog({ supplierProductId, onClose }) {
    const [sp, setSp] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showReceptions, setShowReceptions] = useState(false);

    useEffect(() => {
        if (!supplierProductId) {
            setSp(null);
            return;
        }
        setLoading(true);
        supplierProductsAPI.getDetail(supplierProductId)
            .then(res => setSp(res.data))
            .catch(() => setSp(null))
            .finally(() => setLoading(false));
    }, [supplierProductId]);

    const open = !!supplierProductId;
    const parentAllergens = sp?.industrial_product?.allergens || [];
    const effectiveAllergens = sp?.allergens != null ? sp.allergens : parentAllergens;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="sp-preview-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-slate-600" />
                        {sp?.supplier_name_product || 'Produit fournisseur'}
                    </DialogTitle>
                    <DialogDescription>
                        {sp?.supplier_name}
                    </DialogDescription>
                </DialogHeader>

                {loading && (
                    <div className="py-10 flex items-center justify-center text-slate-400 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
                    </div>
                )}

                {!loading && sp && (
                    <div className="space-y-4 py-2">
                        {/* Photo */}
                        <div className="flex justify-center">
                            {sp.image_url ? (
                                <div className="rounded-lg overflow-hidden border border-slate-200 bg-white" style={{ maxHeight: sp.image_height ? `${sp.image_height}px` : '144px' }}>
                                    <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${sp.image_url}`}
                                        alt={sp.supplier_name_product}
                                        className="max-w-full h-full object-contain mx-auto"
                                        style={{ maxHeight: sp.image_height ? `${sp.image_height}px` : '144px' }}
                                    />
                                </div>
                            ) : (
                                <div className="w-48 h-36 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50">
                                    <Package className="w-12 h-12 text-slate-300" />
                                </div>
                            )}
                        </div>

                        {/* Info Grid */}
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                            <div className="col-span-2 grid grid-cols-3 gap-x-4 items-start">
                                <div>
                                    <p className="text-xs text-slate-400 uppercase tracking-wider">Fournisseur</p>
                                    <p className="font-medium text-slate-900">{sp.supplier_name}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 uppercase tracking-wider">Référence</p>
                                    <p className="font-mono text-slate-700">{sp.supplier_ref || '-'}</p>
                                </div>
                                <div className="flex items-center justify-end">
                                    <span
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium select-none ${
                                            sp.commandable !== false
                                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                                : 'bg-red-50 border-red-200 text-red-600'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={sp.commandable !== false}
                                            disabled
                                            className="rounded border-slate-300 pointer-events-none"
                                        />
                                        Commandable
                                    </span>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wider">Désignation fournisseur</p>
                                <p className="font-medium text-slate-900">{sp.supplier_name_product || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wider">Marque</p>
                                <p className="text-slate-700">{sp.brand || '-'}</p>
                            </div>
                            {effectiveAllergens.length > 0 && (
                                <div className="col-span-2">
                                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Allergènes</p>
                                    <div className="flex flex-wrap gap-1">
                                        {effectiveAllergens.map((a) => (
                                            <span key={a} className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded-full border border-orange-200">
                                                {a}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wider">Unité de facturation</p>
                                <p className="text-slate-700">{sp.invoice_unit || sp.billing_unit || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wider">Colisage</p>
                                <p className="text-slate-700">
                                    {sp.packaging_qty && sp.order_unit && sp.billing_unit
                                        ? `${sp.order_unit} de ${sp.packaging_qty} ${sp.billing_unit}`
                                        : '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wider">Conditionnement de vente</p>
                                <p className="text-slate-700">
                                    {sp.sell_by_detail
                                        ? (sp.billing_unit || 'pièce')
                                        : sp.packaging_qty && sp.order_unit && sp.billing_unit
                                            ? `${sp.order_unit} de ${sp.packaging_qty} ${sp.billing_unit}`
                                            : (sp.order_unit || '-')}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wider">Contenance</p>
                                <p className="text-slate-700">
                                    {sp.capacity_value && sp.capacity_unit
                                        ? `${sp.capacity_value} ${sp.capacity_unit}`
                                        : '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wider">TVA</p>
                                <p className="text-slate-700">
                                    {String(sp.vat_rate ?? sp.industrial_product?.vat_rate ?? 2.1).replace('.', ',')} %
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wider">Prix unitaire</p>
                                <p className="font-semibold text-emerald-600 text-base">
                                    {sp.unit_price
                                        ? `${formatCurrency(sp.unit_price)} / ${sp.invoice_unit || sp.billing_unit || 'unité'}`
                                        : '-'}
                                </p>
                            </div>
                            {sp.unit_price && sp.capacity_value && sp.capacity_value > 0 && (
                                <div className="col-span-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                                    <p className="text-xs text-emerald-600 uppercase tracking-wider">Tarif / Contenance</p>
                                    <p className="font-bold text-emerald-700 text-lg">
                                        {formatCurrency(sp.unit_price / sp.capacity_value)} / {sp.capacity_unit || 'u'}
                                    </p>
                                </div>
                            )}
                        </div>

                        <PriceHistoryPanel supplierProductId={sp.id} />

                        <Button
                            variant="outline"
                            className="w-full mt-2"
                            onClick={() => setShowReceptions(true)}
                            data-testid="view-receptions-btn"
                        >
                            <History className="w-4 h-4 mr-2" />
                            Voir les réceptions de ce produit
                        </Button>
                    </div>
                )}

                <div className="flex justify-end pt-2 sm:hidden">
                    <Button variant="outline" size="sm" onClick={onClose} data-testid="sp-preview-close-btn">
                        Fermer
                    </Button>
                </div>
            </DialogContent>

            <ProductReceptionsDrawer
                open={showReceptions}
                onClose={() => setShowReceptions(false)}
                supplierProductId={sp?.id || null}
                productName={sp?.industrial_product?.name || sp?.supplier_name_product || ''}
                subtitle={`${sp?.supplier_name || ''} · ${sp?.supplier_name_product || ''}`}
            />
        </Dialog>
    );
}
